import type { Address } from '@morpho-org/blue-sdk';
import { type InterfaceToken, parseApiAsset, type TokenAmountUsd } from '@morpho-org/dapp-core';
import { skipToken, useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { RewardContextProps } from '@/contexts/Reward/Reward.context';
import { useWeb3Context } from '@/contexts/Web3Context';
import { addOptionalUsd } from '@/helpers/reward/addOptionalUsd';
import { calculateMorphoClaimable } from '@/helpers/reward/calculateMorphoClaimable';
import { orpc } from '@/lib/orpc/client';
import { errorCodeOf } from '@/lib/orpc/errors';
const REWARDS_METADATA_BATCH_SIZE = 100;
export interface BadgeDetails {
  label: string;
  tooltip?: string;
  href?: string;
}
export interface BaseRewardsTableRowData {
  type: string;
  rewardToken: {
    token: InterfaceToken;
    badges?: BadgeDetails[];
  };
  claimable?: Partial<TokenAmountUsd>;
  accruing?: Partial<TokenAmountUsd>;
  // Commented out until API is fixed
  // vaultOrMarket?: Fetchable<Pick<RewardsMetadata, "vault" | "market">>;
}
export type RewardsTableRowData = BaseRewardsTableRowData & {
  type: 'merkl-reward';
};
export const useRewardsTable = (overrideAddress?: Address): RewardContextProps => {
  const {
    account
  } = useWeb3Context();
  const address = overrideAddress ?? account.address;
  const {
    data: rewardsMerklData,
    refetch: refetchMerkl,
    isLoading: isMerklLoading,
    error: merklError
  } = useQuery(orpc.external.userRewards.queryOptions({
    input: address ? {
      address
    } : skipToken
  }));
  const refetchRewards = useCallback(() => {
    refetchMerkl();
  }, [refetchMerkl]);
  const rawUserRewardsMerkl = rewardsMerklData?.items;
  const rewardTokens = useMemo(() => {
    if (isMerklLoading) return undefined;
    const byKey = new Map(rawUserRewardsMerkl?.map(({
      token
    }) => [`${token.chainId}:${token.address.toLowerCase()}`, {
      chainId: token.chainId,
      address: token.address
    }]));
    return [...byKey.values()];
  }, [rawUserRewardsMerkl, isMerklLoading]);
  const rewardTokenBatches = useMemo(() => {
    const batches: NonNullable<typeof rewardTokens>[] = [];
    for (let i = 0; i < (rewardTokens?.length ?? 0); i += REWARDS_METADATA_BATCH_SIZE) batches.push(rewardTokens!.slice(i, i + REWARDS_METADATA_BATCH_SIZE));
    return batches;
  }, [rewardTokens]);

  // A failed batch only drops metadata for its own tokens, which then fall back to Merkl's.
  const {
    rewardsMetadataResult,
    loadingMetadata
  } = useQueries({
    queries: rewardTokenBatches.map(tokens => orpc.token.rewardsMetadata.queryOptions({
      input: {
        tokens
      }
    })),
    combine: results => ({
      rewardsMetadataResult: results.some(result => result.data) ? {
        items: results.flatMap(result => result.data?.items ?? [])
      } : undefined,
      loadingMetadata: results.some(result => result.isLoading)
    })
  });
  const _rewards = useMemo(() => {
    if (!address) return null;
    if (isMerklLoading) return undefined;
    if (!rewardsMetadataResult) return {
      rewardsMerkl: rawUserRewardsMerkl?.map(r => ({
        ...r,
        metadata: null
      }))
    };
    return {
      rewardsMerkl: rawUserRewardsMerkl?.map(reward => {
        const metadata = rewardsMetadataResult.items.find(token => token.chainId === reward.token.chainId && token.address.toLowerCase() === reward.token.address.toLowerCase());
        const asset = metadata && {
          address: metadata.address,
          symbol: metadata.symbol ?? reward.token.symbol,
          name: metadata.name ?? reward.token.name ?? reward.token.symbol,
          decimals: metadata.decimals,
          tags: metadata.tags,
          logoURI: metadata.logoUri,
          chain: {
            id: metadata.chainId
          }
        };
        return {
          ...reward,
          metadata: {
            asset
          }
        };
      })
    };
  }, [address, rawUserRewardsMerkl, rewardsMetadataResult, isMerklLoading]);
  const displayRewardsMerkl = useMemo(() => {
    if (!_rewards) return [];
    const byIdByToken = new Map<string, RewardsTableRowData>();
    _rewards?.rewardsMerkl?.forEach(reward => {
      const {
        claimable,
        accruing
      } = calculateMorphoClaimable(reward);
      if (!claimable && !accruing) return;
      const id = `${reward.token.address}`;
      const token: InterfaceToken = reward.metadata?.asset ? parseApiAsset({
        ...reward.metadata?.asset,
        price: reward.token.price == null ? null : {
          usd: reward.token.price
        }
      }) : parseApiAsset({
        address: reward.token.address,
        symbol: reward.token.symbol,
        name: reward.token.name ?? reward.token.symbol,
        decimals: reward.token.decimals,
        chain: {
          id: reward.token.chainId
        },
        logoURI: null,
        price: reward.token.price == null ? null : {
          usd: reward.token.price
        }
      });
      const claimableUsd = token?.toUsd(claimable);
      const accruingUsd = token?.toUsd(accruing);
      if (byIdByToken.has(id)) {
        const data = byIdByToken.get(id)!;
        data.accruing!.amount! += accruing;
        data.accruing!.amountUsd = addOptionalUsd(data.accruing!.amountUsd, accruingUsd);
        data.claimable!.amount! += claimable;
        data.claimable!.amountUsd = addOptionalUsd(data.claimable!.amountUsd, claimableUsd);
        return byIdByToken.set(id, data);
      }
      byIdByToken.set(id, {
        type: 'merkl-reward',
        claimable: {
          amount: claimable,
          amountUsd: claimableUsd,
          token: token
        },
        accruing: {
          amount: accruing,
          amountUsd: accruingUsd,
          token: token
        },
        rewardToken: {
          token: token
        }
      });
    });
    return [...byIdByToken.values()];
  }, [_rewards]);
  return {
    display: {
      rewardsMerkl: displayRewardsMerkl,
      rewardsMerged: displayRewardsMerkl
    },
    txData: {
      rewardsMerkl: _rewards?.rewardsMerkl ?? []
    },
    loading: loadingMetadata || isMerklLoading,
    errorCode: rewardsMerklData || !merklError ? null : errorCodeOf(merklError),
    refetchRewards
  };
};